import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { baseurl } from "../../config/url";

const ITEMS_PER_PAGE = 20;

const getToken = () => {
  // supports adminToken + token + auth.token
  const t1 = localStorage.getItem("adminToken");
  const t2 = localStorage.getItem("token");
  if (t1) return t1;
  if (t2) return t2;

  try {
    return JSON.parse(localStorage.getItem("auth") || "{}")?.token || "";
  } catch {
    return "";
  }
};

const safeText = (v) => (v === null || v === undefined || v === "" ? "N/A" : String(v));
const formatINR = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "N/A";
  return `₹${n.toLocaleString("en-IN")}`;
};

const ApproveProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState("");

  // Filters + pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [saleTypeFilter, setSaleTypeFilter] = useState("all");

  // ✅ Fetch unapproved products (same endpoint)
  useEffect(() => {
    const fetchUnapprovedProducts = async () => {
      try {
        setLoading(true);
        setError("");

        const token = getToken();
        const res = await axios.get(`${baseurl}/api/admin/products`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          withCredentials: true,
        });

        const list = Array.isArray(res.data?.products) ? res.data.products : [];
        const unapproved = list.filter((p) => !p?.approved);

        setProducts(unapproved);
      } catch (err) {
        console.error("Fetch products error:", err.response?.data || err.message);
        setError(err.response?.data?.message || "Error fetching products");
      } finally {
        setLoading(false);
      }
    };

    fetchUnapprovedProducts();
  }, []);

  // ✅ View product details (same endpoint)
  const handleViewProduct = async (productId) => {
    try {
      setDetailsLoading(true);
      setError("");

      const token = getToken();
      const res = await axios.get(`${baseurl}/api/admin/product-details/${productId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        withCredentials: true,
      });

      // backend can return {product: {...}} or direct
      const productData = res.data?.product || res.data;
      setSelectedProduct(productData);
    } catch (err) {
      console.error("View product error:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Error fetching product details");
    } finally {
      setDetailsLoading(false);
    }
  };

  // ✅ Approve product (same endpoint)
  const handleApproveProduct = async (productId) => {
    const ok = window.confirm("Approve this product?");
    if (!ok) return;

    try {
      const token = getToken();
      await axios.put(
        `${baseurl}/api/admin/approve-product/${productId}`,
        {},
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          withCredentials: true,
        }
      );

      setProducts((prev) => prev.filter((p) => p._id !== productId));
      alert("✅ Product approved successfully!");
      setSelectedProduct(null);
    } catch (err) {
      console.error("Approve error:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Error approving product");
    }
  };

  const closeSidebar = () => setSelectedProduct(null);

  // ✅ Category dropdown options (unique + safe)
  const categoryOptions = useMemo(() => {
    const map = new Map(); // id -> name
    products.forEach((p) => {
      const id = p?.category?._id;
      const name = p?.category?.name;
      if (id && !map.has(id)) map.set(id, name || "Unnamed");
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [products]);

  // ✅ Filter list
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return products.filter((p) => {
      const name = (p?.name || "").toLowerCase();
      const sellerName = (p?.seller?.name || "").toLowerCase();
      const companyName = (p?.seller?.companyName || "").toLowerCase();

      const matchesSearch =
        !q || name.includes(q) || sellerName.includes(q) || companyName.includes(q);

      const matchesCategory =
        categoryFilter === "all" || p?.category?._id === categoryFilter;

      const matchesSaleType =
        saleTypeFilter === "all" || p?.saleType === saleTypeFilter;

      return matchesSearch && matchesCategory && matchesSaleType;
    });
  }, [products, searchQuery, categoryFilter, saleTypeFilter]);

  // reset page when filters change
  useEffect(() => setCurrentPage(1), [searchQuery, categoryFilter, saleTypeFilter]);

  // ✅ Pagination
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => {
    if (currentPage !== safePage) setCurrentPage(safePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safePage]);

  // ✅ Image url builder (keeps your logic but safer)
  const buildImageUrl = (img) => {
    if (!img) return "";
    if (typeof img === "string") return img;
    if (typeof img === "object" && img._id) return `${baseurl}/api/image/upload/${img._id}`;
    return "";
  };

  const sellerAddressText = (seller) => {
    const a = seller?.address;
    if (!a) return "N/A";
    const parts = [
      a.street,
      a.city,
      a.state,
      a.country,
      a.postalCode ? `- ${a.postalCode}` : "",
    ].filter(Boolean);
    return parts.length ? parts.join(", ") : "N/A";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="text-lg font-semibold text-gray-800">Loading products…</div>
          <div className="text-sm text-gray-500 mt-1">Please wait</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-6">
        <div className="bg-white border border-red-200 rounded-xl shadow-sm p-5">
          <div className="text-lg font-semibold text-red-700">Error</div>
          <div className="text-sm text-red-600 mt-1">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-10">
      <div className="relative flex gap-6">
        {/* LEFT: Product list */}
        <div className={`flex-1 bg-white border border-gray-200 rounded-2xl shadow-sm p-5`}>
          {/* Header */}
          <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Waiting For Approval
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Review products and approve them for listing.
              </p>
            </div>

            <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              Total: <b>{products.length}</b> • Showing: <b>{filteredProducts.length}</b>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-500">Sale Type</label>
                <select
                  value={saleTypeFilter}
                  onChange={(e) => setSaleTypeFilter(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-lg bg-white"
                >
                  <option value="all">All</option>
                  <option value="Sale By Seller">Sale By Seller</option>
                  <option value="Sale By OGCS">Sale By OGCS</option>
                </select>
              </div>

              <div>
                <label className="text-xs text-gray-500">Search</label>
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by product / seller / company..."
                  className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500">Category</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-lg bg-white"
                >
                  <option value="all">All</option>
                  {categoryOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3 mt-4">
              <div className="text-sm text-gray-600">
                Page <b>{safePage}</b> of <b>{totalPages}</b>
              </div>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setCategoryFilter("all");
                  setSaleTypeFilter("all");
                }}
                className="px-4 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-100"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Product</th>
                  <th className="px-4 py-3 text-left font-semibold">Max Qty</th>
                  <th className="px-4 py-3 text-left font-semibold">Price</th>
                  <th className="px-4 py-3 text-left font-semibold">Seller</th>
                  <th className="px-4 py-3 text-left font-semibold">Sale Type</th>
                  <th className="px-4 py-3 text-left font-semibold">Contact</th>
                  <th className="px-4 py-3 text-left font-semibold">Company</th>
                  <th className="px-4 py-3 text-right font-semibold">Action</th>
                </tr>
              </thead>

              <tbody>
                {paginatedProducts.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                      No products found for current filters.
                    </td>
                  </tr>
                ) : (
                  paginatedProducts.map((product, index) => (
                    <tr key={product._id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{safeText(product.name)}</div>
                        <div className="text-xs text-gray-500 font-mono break-all">
                          {safeText(product._id)}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-gray-700">{safeText(product.maxquantity)}</td>
                      <td className="px-4 py-3 text-gray-700">{formatINR(product.price)}</td>
                      <td className="px-4 py-3 text-gray-700">{safeText(product?.seller?.name)}</td>
                      <td className="px-4 py-3 text-gray-700">{safeText(product.saleType)}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {product?.seller?.mobile || product?.seller?.email || "N/A"}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{safeText(product?.seller?.companyName)}</td>

                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <button
                            onClick={() => handleViewProduct(product._id)}
                            className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                          >
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          {filteredProducts.length > 0 && (
            <div className="flex justify-center items-center gap-4 mt-6">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={safePage === 1}
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Previous
              </button>

              <span className="text-gray-700 text-sm">
                Page <b>{safePage}</b> of <b>{totalPages}</b>
              </span>

              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={safePage === totalPages}
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* RIGHT: Sidebar */}
        {selectedProduct && (
          <div className="fixed right-0 top-0 h-full w-[420px] bg-white border-l border-gray-200 shadow-2xl p-5 overflow-y-auto z-50">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-black text-2xl"
              onClick={closeSidebar}
              title="Close"
            >
              &times;
            </button>

            <div className="pr-8">
              <h2 className="text-xl font-bold text-gray-900">{safeText(selectedProduct.name)}</h2>
              <div className="text-sm text-gray-500 mt-1">
                {safeText(selectedProduct?.category?.name)}{" "}
                {selectedProduct?.subcategory?.name ? `• ${selectedProduct.subcategory.name}` : ""}
              </div>
            </div>

            {detailsLoading ? (
              <div className="mt-6 text-sm text-gray-600">Loading details…</div>
            ) : (
              <div className="mt-6 space-y-5">
                {/* Quick Info */}
                <div className="grid grid-cols-2 gap-3">
                  <InfoCard label="Price" value={formatINR(selectedProduct.price)} />
                  <InfoCard label="Stock" value={safeText(selectedProduct.stock)} />
                  <InfoCard label="Max Qty" value={safeText(selectedProduct.maxquantity)} />
                  <InfoCard label="Sold" value={safeText(selectedProduct.sold || 0)} />
                  <InfoCard label="HSN" value={safeText(selectedProduct.hsnCode)} />
                  <InfoCard label="Sale Type" value={safeText(selectedProduct.saleType)} />
                </div>

                {/* Description */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <div className="text-xs text-gray-500">Description</div>
                  <div className="text-sm text-gray-800 mt-1 leading-relaxed">
                    {selectedProduct.description || "No description available."}
                  </div>
                </div>

                {/* Specifications */}
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="text-xs text-gray-500">Specifications</div>
                  <ul className="list-disc list-inside mt-2 text-sm text-gray-800 space-y-1">
                    {selectedProduct.specifications?.length > 0 ? (
                      selectedProduct.specifications.map((spec, i) => (
                        <li key={i}>
                          <span className="font-semibold">{safeText(spec.key)}:</span>{" "}
                          {safeText(spec.value)}
                        </li>
                      ))
                    ) : (
                      <li className="text-gray-500">No specifications listed.</li>
                    )}
                  </ul>
                </div>

                {/* Seller */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-gray-900">Seller Information</div>
                    <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
                      {safeText(selectedProduct?.seller?.companyName)}
                    </span>
                  </div>

                  <div className="mt-3 space-y-2 text-sm">
                    <Line label="Name" value={safeText(selectedProduct?.seller?.name)} />
                    <Line label="Email" value={safeText(selectedProduct?.seller?.email)} />
                    <Line label="Mobile" value={safeText(selectedProduct?.seller?.mobile)} />
                    <Line label="Address" value={sellerAddressText(selectedProduct?.seller)} />
                  </div>
                </div>

                {/* Images */}
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="text-xs text-gray-500 mb-2">Product Images</div>
                  {selectedProduct.images?.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {selectedProduct.images.map((img, idx) => {
                        const url = buildImageUrl(img);
                        return (
                          <div
                            key={idx}
                            className="w-full h-28 bg-gray-100 rounded-lg overflow-hidden border border-gray-200"
                          >
                            {url ? (
                              <img
                                src={url}
                                alt={`Product ${idx + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.src = "/placeholder.png";
                                }}
                              />
                            ) : (
                              <div className="h-full flex items-center justify-center text-xs text-red-600">
                                Invalid image
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No images available.</div>
                  )}
                </div>

                {/* Approve */}
                <button
                  onClick={() => handleApproveProduct(selectedProduct._id)}
                  className="w-full px-4 py-3 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700"
                >
                  Approve Product
                </button>

                <button
                  onClick={closeSidebar}
                  className="w-full px-4 py-3 rounded-xl bg-gray-100 text-gray-800 font-semibold hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const InfoCard = ({ label, value }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-3">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-sm font-semibold text-gray-900 mt-1 break-all">{safeText(value)}</div>
  </div>
);

const Line = ({ label, value }) => (
  <div className="flex items-start gap-2">
    <div className="w-20 text-xs text-gray-500">{label}</div>
    <div className="text-sm text-gray-900 font-semibold break-words">{safeText(value)}</div>
  </div>
);

export default ApproveProductsPage;
























// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { baseurl } from "../../config/url";
// import { useMemo } from "react";


// const ApproveProductsPage = () => {
//   const [products, setProducts] = useState([]);
//   const [selectedProduct, setSelectedProduct] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 20;
//   const [searchQuery, setSearchQuery] = useState("");
//   const [categoryFilter, setCategoryFilter] = useState("all");
//   const [saleTypeFilter, setSaleTypeFilter] = useState("all");

//   // Fetch unapproved products
//   // useEffect(() => {
//   //   const fetchUnapprovedProducts = async () => {
//   //     try {
//   //       const response = await axios.get(`${baseurl}/api/admin/products`);
//   //       //console.log("All fetched products:", response.data.products);
//   //       // const unapprovedProducts = response.data.products.filter(
//   //       //   (product) => product.approved === false
//   //       // );
//   //       const unapprovedProducts = response.data.products; // temporarily show all
//   //       console.log("Fetched products:", response.data.products);
//   //       setProducts(unapprovedProducts);
//   //     } catch (err) {
//   //       setError(err.response ? err.response.data.message : "Error fetching products");
//   //     } finally {
//   //       setLoading(false);
//   //     }
//   //   };

//   //   fetchUnapprovedProducts();
//   // }, []);
//   // Fetch unapproved products
//   useEffect(() => {
//     const fetchUnapprovedProducts = async () => {
//       try {
//         const response = await axios.get(`${baseurl}/api/admin/products`, {
//           headers: {
//             Authorization: `Bearer ${localStorage.getItem("token")}`,
//           },
//         });

//         const unapprovedProducts = response.data.products.filter(
//           (p) => !p.approved
//         );
//         setProducts(unapprovedProducts);
//         console.log("Fetched products:", unapprovedProducts);
//       } catch (err) {
//         setError(
//           err.response ? err.response.data.message : "Error fetching products"
//         );
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchUnapprovedProducts();
//   }, []);

//   const handleApproveProduct = async (productId) => {
//     try {
//       await axios.put(
//         `${baseurl}/api/admin/approve-product/${productId}`,
//         {},
//         {
//           headers: {
//             Authorization: `Bearer ${localStorage.getItem("token")}`,
//           },
//         }
//       );
//       setProducts((prevProducts) =>
//         prevProducts.filter((product) => product._id !== productId)
//       );
//       alert("Product approved successfully!");
//       setSelectedProduct(null); // Close the sidebar
//     } catch (err) {
//       alert(
//         err.response ? err.response.data.message : "Error approving product"
//       );
//     }
//   };

//   // const handleViewProduct = async (productId) => {
//   //   try {
//   //     const response = await axios.get(`${baseurl}/api/admin/product-details/${productId}`);
//   //     setSelectedProduct(response.data);
//   //   } catch (err) {
//   //     alert(err.response ? err.response.data.message : "Error fetching product details");
//   //   }
//   // };

//   const handleViewProduct = async (productId) => {
//     try {
//       const response = await axios.get(
//         `${baseurl}/api/admin/product-details/${productId}`,
//         {
//           headers: {
//             Authorization: `Bearer ${localStorage.getItem("token")}`, // Pass the admin token
//           },
//         }
//       );
//       setSelectedProduct(response.data);
//     } catch (err) {
//       alert(
//         err.response
//           ? err.response.data.message
//           : "Error fetching product details"
//       );
//     }
//   };

//   const closeSidebar = () => {
//     setSelectedProduct(null);
//   };

//   const filteredProducts = useMemo(() => {
//     return products.filter((product) => {
//       const matchesSearch = product.name
//         .toLowerCase()
//         .includes(searchQuery.toLowerCase());
//       const matchesCategory =
//         categoryFilter === "all" || product.category?._id === categoryFilter;
//       const matchesSaleType =
//         saleTypeFilter === "all" || product.saleType === saleTypeFilter;
//       return matchesSearch && matchesCategory && matchesSaleType;
//     });
//   }, [products, searchQuery, categoryFilter, saleTypeFilter]);

//   const paginatedProducts = useMemo(() => {
//     const indexOfLastItem = currentPage * itemsPerPage;
//     const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//     return filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
//   }, [filteredProducts, currentPage, itemsPerPage]);


//   if (loading) {
//     return (
//       <div className="flex items-center justify-center h-screen bg-gray-100">
//         <p className="text-xl font-semibold text-blue-600 animate-pulse">
//           Loading...
//         </p>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="flex items-center justify-center h-screen bg-gray-100">
//         <p className="text-xl font-semibold text-red-600">Error: {error}</p>
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-gradient-to-r from-gray-50 to-blue-50 py-10 px-4 lg:px-16">
//       <div className="relative flex">
//         {/* Product List */}
//         <div
//           className={`flex-1 p-6 bg-white rounded-lg shadow-lg ${selectedProduct ? "mr-96" : ""
//             }`}
//         >
//           <h1 className="text-4xl font-extrabold text-center text-gray-800 mb-12">
//             Approve Products
//           </h1>
//           <div className="overflow-x-auto bg-white rounded-lg shadow-md">
//             {/* Filters Section */}

//             <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
//               {/* Sale Type Filter Dropdown */}
//               <select
//                 value={saleTypeFilter}
//                 onChange={(e) => {
//                   setSaleTypeFilter(e.target.value);
//                   setCurrentPage(1); // Reset pagination
//                 }}
//                 className="w-full md:w-1/4 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
//               >
//                 <option value="all">All Sale Types</option>
//                 <option value="Sale By Seller">Sale By Seller</option>
//                 <option value="Sale By OGCS">Sale By OGCS</option>
//               </select>

//               {/* Search Bar */}
//               <input
//                 type="text"
//                 placeholder="Search by product name"
//                 value={searchQuery}
//                 onChange={(e) => {
//                   setSearchQuery(e.target.value);
//                   setCurrentPage(1); // Reset to page 1 on new search
//                 }}
//                 className="w-full md:w-1/2 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
//               />

//               {/* Category Filter Dropdown (optional) */}
//               <select
//                 value={categoryFilter}
//                 onChange={(e) => {
//                   setCategoryFilter(e.target.value);
//                   setCurrentPage(1); // Reset to page 1 on new filter
//                 }}
//                 className="w-full md:w-1/4 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
//               >
//                 <option value="all">All Categories</option>
//                 {[...new Set(products.map((p) => p.category?._id))]
//                   .filter(Boolean)
//                   .map((categoryId) => {
//                     const category = products.find(
//                       (p) => p.category?._id === categoryId
//                     )?.category;
//                     return (
//                       <option key={categoryId} value={categoryId}>
//                         {category?.name || "Unnamed"}
//                       </option>
//                     );
//                   })}
//               </select>
//             </div>

//             <table className="table-auto w-full text-sm border-collapse">
//               <thead>
//                 <tr className="bg-gradient-to-r from-blue-400 to-blue-500 text-white">
//                   <th className="px-6 py-4 text-left font-bold">
//                     Product Name
//                   </th>
//                   <th className="px-6 py-4 text-left font-bold">
//                     Max Quantity
//                   </th>
//                   <th className="px-6 py-4 text-left font-bold">Price</th>
//                   <th className="px-6 py-4 text-left font-bold">Seller</th>
//                   <th className="px-6 py-4 text-left font-bold">Sale Type</th>
//                   <th className="px-6 py-4 text-left font-bold">Contact</th>
//                   <th className="px-6 py-4 text-center font-bold">Action</th>
//                   <th className="px-6 py-4 text-center font-bold">
//                     Company Name
//                   </th>
//                 </tr>
//               </thead>
//               {/* <tbody>
//                 {products.length > 0 ? (
//                   products.map((product, index) => (
//                     <tr
//                       key={product._id}
//                       className={`${
//                         index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
//                       } hover:bg-gray-200 transition-colors duration-300`}
//                     >
//                       <td className="px-6 py-4 text-gray-800 font-semibold">
//                         {product.name}
//                       </td>
//                       <td className="px-6 py-4 text-gray-700">
//                         {product.maxquantity}
//                       </td>
//                       <td className="px-6 py-4 text-gray-700">
//                         ₹{product.price}
//                       </td>
//                       <td className="px-6 py-4 text-center">
//                         <button
//                           onClick={() => handleViewProduct(product._id)}
//                           className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-1 px-4 rounded-lg hover:from-blue-600 hover:to-blue-700 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 transition-transform duration-300 transform hover:scale-105"
//                         >
//                           View
//                         </button>
//                       </td>
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td
//                       colSpan="4"
//                       className="px-6 py-4 text-center text-gray-600 font-medium"
//                     >
//                       No unapproved products found.
//                     </td>
//                   </tr>
//                 )}
//               </tbody> */}
//               <tbody>
//                 {products.length > 0 ? (
//                   // products.map((product, index) => (
//                   paginatedProducts.map((product, index) => (
//                     <tr
//                       key={product._id}
//                       className={`${index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
//                         } hover:bg-gray-200 transition-colors duration-300`}
//                     >
//                       <td className="px-6 py-4 text-gray-800 font-semibold">
//                         {product.name}
//                       </td>
//                       <td className="px-6 py-4 text-gray-700">
//                         {product.maxquantity}
//                       </td>
//                       <td className="px-6 py-4 text-gray-700">
//                         ₹{product.price}
//                       </td>
//                       <td className="px-6 py-4 text-gray-700">
//                         {product.seller?.name || "N/A"}
//                       </td>
//                       <td className="px-6 py-4 text-gray-700">
//                         {product.saleType || "N/A"}
//                       </td>
//                       <td className="px-6 py-4 text-gray-700">
//                         {product.seller?.mobile ||
//                           product.seller?.email ||
//                           "N/A"}
//                       </td>
//                       <td className="px-6 py-4 text-center">
//                         <button
//                           onClick={() => handleViewProduct(product._id)}
//                           className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-1 px-4 rounded-lg hover:from-blue-600 hover:to-blue-700 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 transition-transform duration-300 transform hover:scale-105"
//                         >
//                           View
//                         </button>
//                       </td>
//                       <td className="px-6 py-4 text-gray-700">
//                         {product.seller?.companyName || "N/A"}
//                       </td>
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td
//                       colSpan="7"
//                       className="px-6 py-4 text-center text-gray-600 font-medium"
//                     >
//                       No unapproved products found.
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>

//           <div className="flex justify-center items-center gap-4 mt-6">
//             <button
//               onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
//               disabled={currentPage === 1}
//               className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
//             >
//               Previous
//             </button>
//             <span className="text-gray-700">
//               Page {currentPage} of{" "}
//               {Math.ceil(filteredProducts.length / itemsPerPage)}
//             </span>
//             <button
//               onClick={() =>
//                 setCurrentPage((prev) =>
//                   prev < Math.ceil(filteredProducts.length / itemsPerPage)
//                     ? prev + 1
//                     : prev
//                 )
//               }
//               disabled={
//                 currentPage ===
//                 Math.ceil(filteredProducts.length / itemsPerPage)
//               }
//               className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
//             >
//               Next
//             </button>
//           </div>
//         </div>

//         {/* Sidebar for Product Details */}
//         {selectedProduct && (
//           <div className="fixed right-0 top-0 h-full w-96 bg-white rounded-lg shadow-lg p-6 overflow-y-auto">
//             <button
//               className="absolute top-4 right-4 text-gray-500 hover:text-black text-2xl"
//               onClick={closeSidebar}
//             >
//               &times;
//             </button>
//             <h2 className="text-3xl font-bold text-gray-800 mb-6">
//               {selectedProduct.name}
//             </h2>
//             <div className="space-y-4 text-gray-700">
//               <p>
//                 <span className="font-semibold text-blue-600">
//                   Description:
//                 </span>{" "}
//                 {selectedProduct.description || "No description available"}
//               </p>
//               <p>
//                 <span className="font-semibold text-blue-600">Price:</span> ₹
//                 {selectedProduct.price} (
//                 {selectedProduct.priceUnit || "per unit"})
//               </p>
//               <p>
//                 <span className="font-semibold text-blue-600">
//                   Max Quantity:
//                 </span>{" "}
//                 {selectedProduct.maxquantity || "N/A"}
//               </p>
//               <p>
//                 <span className="font-semibold text-blue-600">Category:</span>{" "}
//                 {selectedProduct.category?.name || "Uncategorized"}
//               </p>
//               <p className="text-gray-700">
//                 <span className="font-semibold text-blue-600">
//                   Subcategory:
//                 </span>{" "}
//                 {selectedProduct.subcategory?.name || "Not specified"}
//               </p>
//               <p className="text-gray-700">
//                 <span className="font-semibold text-blue-600">Size:</span>{" "}
//                 {selectedProduct.size || "Standard"}
//               </p>
//               <p className="text-gray-700">
//                 <span className="font-semibold text-blue-600">Sold Units:</span>{" "}
//                 {selectedProduct.sold || 0}
//               </p>
//               <p className="text-gray-700">
//                 <span className="font-semibold text-blue-600">HSN Code:</span>{" "}
//                 {selectedProduct.hsnCode || "Not available"}
//               </p>
//               <p className="text-gray-700">
//                 <span className="font-semibold text-blue-600">
//                   Available Stock:
//                 </span>{" "}
//                 {selectedProduct.stock || "Out of stock"}
//               </p>

//               <div>
//                 <span className="font-semibold text-blue-600">
//                   Specifications:
//                 </span>
//                 <ul className="list-disc list-inside">
//                   {selectedProduct.specifications?.length > 0 ? (
//                     selectedProduct.specifications.map((spec, index) => (
//                       <li key={index}>
//                         <span className="font-semibold">{spec.key}:</span>{" "}
//                         {spec.value}
//                       </li>
//                     ))
//                   ) : (
//                     <li>No specifications listed</li>
//                   )}
//                 </ul>
//               </div>
//               <p className="text-gray-700">
//                 {/* <span className="font-semibold text-green-600">👨‍💼 Seller Information:</span> {selectedProduct.seller?.name || "Not available"} */}
//                 <h3 className="text-xl font-bold text-green-600 mt-6 mb-2">
//                   👨‍💼 Seller Information
//                 </h3>
//                 <p className="text-gray-700">
//                   <span className="font-semibold text-blue-600">Name:</span>{" "}
//                   {selectedProduct.seller?.name || "N/A"}
//                 </p>
//                 <p className="text-gray-700">
//                   <span className="font-semibold text-blue-600">Company:</span>{" "}
//                   {selectedProduct.seller?.companyName || "N/A"}
//                 </p>
//                 <p className="text-gray-700">
//                   <span className="font-semibold text-blue-600">Email:</span>{" "}
//                   {selectedProduct.seller?.email || "N/A"}
//                 </p>
//                 <p className="text-gray-700">
//                   <span className="font-semibold text-blue-600">Mobile:</span>{" "}
//                   {selectedProduct.seller?.mobile || "N/A"}
//                 </p>
//                 {selectedProduct.seller?.address
//                   ? `${selectedProduct.seller.address?.street || ""}, ${selectedProduct.seller.address?.city || ""
//                   }, ${selectedProduct.seller.address?.state || ""}, ${selectedProduct.seller.address?.country || ""
//                   } - ${selectedProduct.seller.address?.postalCode || ""}`
//                   : "N/A"}

//                 <p className="text-gray-700">
//                   <span className="font-semibold text-blue-600">
//                     Sale Type:
//                   </span>{" "}
//                   {selectedProduct.saleType || "N/A"}
//                 </p>
//               </p>
//               <div>
//                 <p className="font-semibold text-green-600">
//                   🖼️ Product Images:
//                 </p>
//                 <div className="grid grid-cols-1 gap-2">
//                   {/* {selectedProduct.images?.length > 0 ? (
//                     selectedProduct.images.map((image, index) => (
//                       <div
//                         key={index}
//                         className="w-full h-40 bg-gray-100 rounded-lg shadow-md overflow-hidden"
//                       >
//                         <img
//                           // src={`data:${image.contentType};base64,${image.data}`}
//                           src={image}
//                           alt={`Product Image ${index + 1}`}
//                           className="w-full h-full object-cover"
//                         />
//                       </div>
//                     ))
//                   ) : (
//                     <p className="text-gray-500">No images available</p>
//                   )} */}
//                   {selectedProduct.images?.length > 0 ? (
//                     selectedProduct.images.map((imageObj, index) => {
//                       const imageUrl = typeof imageObj === "string"
//                         ? imageObj
//                         : typeof imageObj === "object" && imageObj !== null && imageObj._id
//                           ? `${baseurl}/api/image/upload/${imageObj._id}`
//                           : "";

//                       return (
//                         <div
//                           key={index}
//                           className="w-full h-40 bg-gray-100 rounded-lg shadow-md overflow-hidden"
//                         >
//                           {imageUrl ? (
//                             <img
//                               src={imageUrl}
//                               alt={`Product Image ${index + 1}`}
//                               onError={(e) => {
//                                 console.error("Image load error:", imageUrl);
//                                 e.target.src = "/placeholder.png";
//                               }}
//                               className="w-full h-full object-cover"
//                             />
//                           ) : (
//                             <p className="text-red-500 text-sm">Invalid image</p>
//                           )}
//                         </div>
//                       );
//                     })
//                   ) : (
//                     <p className="text-gray-500">No images available</p>
//                   )}

//                 </div>
//               </div>
//               <div className="text-center">
//                 <button
//                   onClick={() => handleApproveProduct(selectedProduct._id)}
//                   className="bg-gradient-to-r from-green-500 to-green-600 text-white py-2 px-6 rounded-lg hover:from-green-600 hover:to-green-700 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-1 transition-transform duration-300 transform hover:scale-105"
//                 >
//                   Approve Product
//                 </button>
//               </div>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ApproveProductsPage;
